// Monobank Payment Service
// Proxied through /monobank-api.php — pricing and payment confirmation happen server-side

const PROXY_URL = '/monobank-api.php';

/**
 * Create an order and its invoice. The server prices the cart itself and only
 * accepts a total that matches `expectedTotal`.
 * @param {Object} params
 * @param {Array} params.items - [{ toolId, rentalType, duration }]
 * @param {string} params.vendingMachineId
 * @param {number} params.expectedTotal - Total shown to the customer, UAH
 * @param {string} params.identityToken - Issued by otp-api.php or diia-api.php
 * @returns {Promise<Object>} - { success, pageUrl, reference } or { success: false, error }
 */
export const createOrder = async (params) => {
  const response = await fetch(`${PROXY_URL}?action=create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.success || !data.pageUrl) {
    return { success: false, error: data?.error || 'payment_unavailable' };
  }
  return { success: true, pageUrl: data.pageUrl, reference: data.reference };
};

/**
 * Ask the server to confirm the payment with Monobank and place the order.
 * Safe to call repeatedly — the order is only ever placed once.
 * @param {string} reference
 * @returns {Promise<string>} - placed | conflict | failed | pending | unknown
 */
export const finalizeOrder = async (reference) => {
  try {
    const response = await fetch(`${PROXY_URL}?action=finalize-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reference }),
    });
    const data = await response.json();
    return data.status || 'pending';
  } catch (error) {
    console.error('Monobank finalize error:', error);
    return 'pending';
  }
};

/**
 * Charge a saved card token (merchant-initiated, no user interaction)
 */
export const chargeCardToken = async ({ orderId, amount, comment, password }) => {
  const res = await fetch('/tools-api.php?action=admin-charge-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${password || ''}`,
    },
    body: JSON.stringify({ orderId, amount, comment }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || 'Charge failed');
  return data;
};

/**
 * Format price for display
 * @param {number} amount - Amount in UAH
 * @returns {string} - Formatted price string
 */
export const formatPrice = (amount) => {
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency',
    currency: 'UAH',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const monobankPayment = {
  createOrder,
  finalizeOrder,
  formatPrice,
};

export default monobankPayment;
